// pages/Show/Media/TaggedMediaPage.jsx

import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { gsap } from "gsap";
import { useGSAP } from "@gsap/react";

import ShowSidebar from "../Sidebar/ShowSidebar";
import ShowAwardsSidebarContainer from "../Awards/components/ShowAwardsSidebarContainer";
import MediaSection from "./MediaSection";

import {
  getShowAwards,
  getShowDetailPage,
} from "../../../utils/api/helpers/show";
import { getShowMediaByTag } from "../../../utils/api/helpers/media";

import { S3URL } from "../../../data/globalConstants";
import PageLoader from "../../../utils/Ui/PageLoader";

export default function TaggedMediaPage({ tag, pageTitle, sectionTitle }) {
  const { genreSlug, showSlug } = useParams();
  const navigate = useNavigate();

  const containerRef = useRef(null);
  const bgImageRef = useRef(null);
  const showPageContentRef = useRef(null);

  const [showData, setShowData] = useState(null);
  const [showAwards, setShowAwards] = useState([]);
  const [hasAwards, setHasAwards] = useState(false);
  const [mediaItems, setMediaItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!genreSlug || !showSlug || !tag) return;

    let isMounted = true;

    const loadPage = async () => {
      setIsLoading(true);

      try {
        const [showResult, mediaResult, awardsResult] =
          await Promise.allSettled([
            getShowDetailPage(genreSlug, showSlug),
            getShowMediaByTag(genreSlug, showSlug, tag),
            getShowAwards(genreSlug, showSlug),
          ]);

        if (!isMounted) return;

        if (showResult.status !== "fulfilled") {
          console.error("Unable to load show:", showResult.reason);

          navigate("/", { replace: true });
          return;
        }

        const show = showResult.value?.data;

        const normalizedVisible =
          typeof show?.visible === "string"
            ? show.visible.trim().toLowerCase()
            : show?.visible;

        const isShowDisabled =
          !show ||
          normalizedVisible === false ||
          normalizedVisible === 0 ||
          normalizedVisible === "0" ||
          normalizedVisible === "false" ||
          normalizedVisible === null ||
          normalizedVisible === undefined;

        if (isShowDisabled) {
          navigate("/", { replace: true });
          return;
        }

        setShowData(show);

        if (mediaResult.status === "fulfilled") {
          const items = Array.isArray(mediaResult.value?.data)
            ? mediaResult.value.data
            : [];

          const orderedItems =
            tag.toLowerCase() === "creator-diaries"
              ? [...items].reverse()
              : items;

          setMediaItems(orderedItems);
        } else {
          console.error(
            `Unable to load media tagged "${tag}":`,
            mediaResult.reason,
          );

          setMediaItems([]);
        }

        if (awardsResult.status === "fulfilled") {
          const competitions = awardsResult.value?.data?.competitions || [];

          setShowAwards(competitions);
          setHasAwards(competitions.length > 0);
        } else {
          console.error("Unable to load show awards:", awardsResult.reason);

          setShowAwards([]);
          setHasAwards(false);
        }
      } catch (error) {
        console.error(`Unable to load media tagged "${tag}":`, error);

        if (isMounted) {
          navigate("/", { replace: true });
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadPage();

    return () => {
      isMounted = false;
    };
  }, [genreSlug, showSlug, tag, navigate]);

  useGSAP(
    () => {
      if (isLoading || !showData) return;

      const bgImage = bgImageRef.current;
      const content = showPageContentRef.current;

      if (!bgImage || !content) return;

      gsap.set(bgImage, {
        opacity: 0,
        filter: "blur(5px) brightness(0.6)",
      });

      gsap.set(content, {
        opacity: 0,
        y: 20,
      });

      const timeline = gsap.timeline({
        defaults: {
          ease: "power2.out",
        },
      });

      timeline
        .to(bgImage, {
          opacity: 1,
          duration: 1.2,
        })
        .to(
          content,
          {
            opacity: 1,
            y: 0,
            duration: 1.2,
          },
          "-=0.6",
        );
    },
    {
      dependencies: [isLoading, showData, tag],
      scope: containerRef,
    },
  );

  return (
    <>
      <PageLoader loading={isLoading} />

      <div className="media-page-container" ref={containerRef}>
        {showData?.title && (
          <Helmet>
            <title>
              {`${pageTitle} | ${showData.title} | Netflix Series FYC`}
            </title>

            <meta
              name="description"
              content={`${pageTitle} for ${showData.title}`}
            />
          </Helmet>
        )}

        <div className="width-ctrl">
          <div className="show-page-inner" ref={showPageContentRef}>
            <div
              className="show-left-column"
              style={{
                top: !hasAwards ? "33%" : "",
                height: !hasAwards ? "fit-content" : "",
              }}
            >
              <ShowSidebar
                showData={showData}
                genreSlug={genreSlug}
                showSlug={showSlug}
                displayAwards={hasAwards}
                showAwards={showAwards}
              />
            </div>

            <div className="show-right-column">
              <div className="show-main-content">
                {mediaItems.length > 0 ? (
                  <MediaSection
                    slug="video"
                    title={sectionTitle}
                    items={mediaItems}
                  />
                ) : (
                  !isLoading && (
                    <section id={tag} className="show-page-section">
                      <h3 className="show-page-section-title">
                        {sectionTitle}
                      </h3>

                      <p>No media is currently available.</p>
                    </section>
                  )
                )}

                {hasAwards && (
                  <div className="d-block d-sm-none">
                    <ShowAwardsSidebarContainer
                      genreSlug={genreSlug}
                      showSlug={showSlug}
                      showAwards={showAwards}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="show-page-background" ref={bgImageRef}>
          <img
            src={S3URL + showData?.backgroundImage}
            loading="lazy"
            alt={showData?.title || ""}
            className="show-masked-image"
          />
        </div>
      </div>
    </>
  );
}
